import { OptionsOrGroups, PropsValue } from "../select";
import { SelectGroup, SelectOption, SelectValueType } from "./types";

export function parseValue<
  Option extends SelectOption,
  Group extends SelectGroup<Option>,
>(
  value: SelectValueType,
  optionsOrGroups: OptionsOrGroups<Option, Group>,
  multiple: boolean,
): PropsValue<Option> | undefined {
  if (!value) {
    return multiple ? [] : undefined;
  }

  const options = optionsOrGroups.flatMap((option) => {
    if ("options" in option) return option.options;
    return option;
  });

  if (multiple) return options.filter((option) => value?.includes(option.id));
  return options.find((option) => option.id === value) ?? undefined;
}

export function parseChange(
  value: PropsValue<SelectOption>,
  multiple: boolean,
): SelectValueType {
  if (multiple && Array.isArray(value)) return value.map((v) => v.id);
  else if (!multiple && !!value && "id" in value) return value.id;

  return undefined;
}

export function parseOptions<
  Option extends SelectOption,
  Group extends SelectGroup<Option>,
>(
  value: SelectValueType,
  optionsOrGroups: OptionsOrGroups<Option, Group>,
  multiple: boolean,
) {
  const options = optionsOrGroups.flatMap((option) => {
    if ("options" in option) return option.options;
    return option;
  });

  let values: string[] = [];

  if (multiple && !!value && Array.isArray(value)) {
    values = value;
  } else if (!multiple && !!value && !Array.isArray(value)) {
    values = [value];
  }

  const invalidValues = values.filter(
    (value) => !options.some((option) => option.id === value),
  );

  if (!invalidValues.length) {
    return optionsOrGroups;
  }

  const isGrouped = optionsOrGroups.some((option) => "options" in option);

  if (isGrouped) {
    return [
      {
        label: "Invalid",
        options: invalidValues.map((value) => ({
          id: value,
          label: `Invalid (${value})`,
        })),
      },
      ...optionsOrGroups,
    ] as OptionsOrGroups<Option, Group>;
  }

  return [
    ...invalidValues.map((value) => ({
      id: value,
      label: `Invalid (${value})`,
    })),
    ...optionsOrGroups,
  ] as OptionsOrGroups<Option, Group>;
}

export function parseDateValue(value: string | Date | undefined) {
  if (!value) return "";
  let stringValue = value instanceof Date ? value.toISOString() : value;
  return stringValue.slice(0, 10);
}
